
import React, { useEffect, useRef } from 'react';
import { createChat } from '@n8n/chat';
import { useProxyConfig } from '@/contexts/ProxyConfigContext';
import { useTheme } from '@/contexts/ThemeContext';
import { buildLogoUrl } from '@/utils/supabaseConfig';
import { getStorageUrl } from '@/utils/storageUtils';

interface N8nChatProps {
  onClose?: () => void;
}

const N8nChat: React.FC<N8nChatProps> = ({ onClose }) => {
  const { theme } = useTheme();
  const { config } = useProxyConfig();
  const mountRef = useRef<HTMLDivElement>(null);
  const chatInstanceRef = useRef<any>(null);

  const initializeChat = async () => {
    if (!mountRef.current) return;

    try {
      // Получаем CSS переменные из темы приложения
      const computedStyle = getComputedStyle(document.documentElement);
      const primaryColor = computedStyle.getPropertyValue('--primary').trim();
      const secondaryColor = computedStyle.getPropertyValue('--secondary').trim(); 
      const backgroundColor = computedStyle.getPropertyValue('--background').trim();
      const foregroundColor = computedStyle.getPropertyValue('--foreground').trim();
      const cardColor = computedStyle.getPropertyValue('--card').trim();
      
      // Получаем URL логотипа
      const logoUrl = getStorageUrl(buildLogoUrl());
      
      // Применяем кастомные CSS переменные для N8N чата
      const chatStyles = `
        @import url('https://fonts.googleapis.com/css2?family=Russo+One&display=swap');
        
        :root {
          --chat--color-primary: hsl(${primaryColor});
          --chat--color-primary-shade-50: hsl(${primaryColor} / 0.9);
          --chat--color-primary-shade-100: hsl(${primaryColor} / 0.8);
          --chat--color-secondary: hsl(${secondaryColor});
          --chat--color-secondary-shade-50: hsl(${secondaryColor} / 0.9);
          --chat--color-white: hsl(${backgroundColor});
          --chat--color-light: hsl(${cardColor});
          --chat--color-light-shade-50: hsl(${cardColor} / 0.8);
          --chat--color-light-shade-100: hsl(${cardColor} / 0.6);
          --chat--color-medium: hsl(${foregroundColor} / 0.3);
          --chat--color-dark: hsl(${foregroundColor});
          --chat--color-disabled: hsl(${foregroundColor} / 0.5);
          --chat--color-typing: hsl(${foregroundColor} / 0.7);
          
          --chat--window--width: 420px;
          --chat--window--height: 650px;
          --chat--border-radius: 16px;
          --chat--spacing: 1.5rem;
          --chat--transition-duration: 0.4s;
          
          --chat--header--background: linear-gradient(135deg, #064f4f 0%, #086161 50%, #0a7474 100%);
          --chat--header--color: white;
          --chat--header--padding: 1.5rem;
          --chat--header--border-bottom: 1px solid hsl(${primaryColor} / 0.3);
          
          --chat--message--font-size: 0.95rem;
          --chat--message--padding: 1rem 1.25rem;
          --chat--message--border-radius: 12px;
          --chat--message-line-height: 1.6;
          --chat--message--bot--background: hsl(${cardColor});
          --chat--message--bot--color: hsl(${foregroundColor});
          --chat--message--bot--border: 1px solid hsl(${foregroundColor} / 0.1);
          --chat--message--user--background: linear-gradient(135deg, hsl(${primaryColor}) 0%, hsl(${primaryColor} / 0.9) 100%);
          --chat--message--user--color: hsl(${backgroundColor});
          --chat--message--user--border: none;
          
          --chat--toggle--background: linear-gradient(135deg, hsl(${primaryColor}) 0%, hsl(${secondaryColor}) 100%);
          --chat--toggle--hover--background: linear-gradient(135deg, hsl(${primaryColor} / 0.9) 0%, hsl(${secondaryColor} / 0.9) 100%);
          --chat--toggle--active--background: linear-gradient(135deg, hsl(${primaryColor} / 0.8) 0%, hsl(${secondaryColor} / 0.8) 100%);
          --chat--toggle--color: hsl(${backgroundColor});
          --chat--toggle--size: 68px;
          --chat--toggle--box-shadow: 0 8px 32px hsl(${primaryColor} / 0.3);
        }
        
        /* Кастомные стили для красивого оформления */
        [data-n8n-chat] .n8n-chat-window {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px hsl(${foregroundColor} / 0.05) !important;
          backdrop-filter: blur(10px) !important;
        }
        
        [data-n8n-chat] .n8n-chat-header {
          background: var(--chat--header--background) !important;
          position: relative !important;
          overflow: hidden !important;
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          text-align: center !important;
        }
        
        [data-n8n-chat] .n8n-chat-header::before {
          content: '' !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%) !important;
          pointer-events: none !important;
        }
        
        [data-n8n-chat] .n8n-chat-header::after {
          content: '' !important;
          position: absolute !important;
          top: 10px !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
          width: 60px !important;
          height: 60px !important;
          background-image: url('${logoUrl}') !important;
          background-size: contain !important;
          background-repeat: no-repeat !important;
          background-position: center !important;
          filter: brightness(0) invert(1) !important;
          z-index: 2 !important;
        }
        
        [data-n8n-chat] .n8n-chat-title {
          font-family: 'Russo One', sans-serif !important;
          font-weight: 400 !important;
          font-size: 1.4rem !important;
          color: white !important;
          margin-top: 70px !important;
          margin-bottom: 4px !important;
          position: relative !important;
          z-index: 1 !important;
          letter-spacing: 0.5px !important;
        }
        
        [data-n8n-chat] .n8n-chat-subtitle {
          font-family: 'Inter', sans-serif !important;
          font-weight: 500 !important;
          font-size: 0.875rem !important;
          color: rgba(255, 255, 255, 0.85) !important;
          position: relative !important;
          z-index: 1 !important;
          margin-top: 0 !important;
        }
        
        [data-n8n-chat] .n8n-chat-toggle {
          background: var(--chat--toggle--background) !important;
          box-shadow: var(--chat--toggle--box-shadow) !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        
        [data-n8n-chat] .n8n-chat-toggle:hover {
          transform: scale(1.05) !important;
          box-shadow: 0 12px 40px hsl(${primaryColor} / 0.4) !important;
        }
        
        /* Исправляем ТОЛЬКО поле ввода, не трогая сообщения */
        [data-n8n-chat] .n8n-chat-input-container input,
        [data-n8n-chat] .n8n-chat-input-container textarea,
        [data-n8n-chat] .n8n-chat-footer input,
        [data-n8n-chat] .n8n-chat-footer textarea {
          color: hsl(${foregroundColor}) !important;
          background-color: hsl(${backgroundColor}) !important;
          border: 1px solid hsl(${foregroundColor} / 0.2) !important;
        }
        
        /* Стили для кнопки отправки - цвет логотипа */
        [data-n8n-chat] .n8n-chat-send-button,
        [data-n8n-chat] button[type="submit"],
        [data-n8n-chat] .n8n-chat-footer button {
          background: linear-gradient(135deg, #064f4f 0%, #086161 50%, #0a7474 100%) !important;
          color: white !important;
          border: none !important;
          opacity: 1 !important;
        }
        
        [data-n8n-chat] .n8n-chat-send-button:hover,
        [data-n8n-chat] button[type="submit"]:hover,
        [data-n8n-chat] .n8n-chat-footer button:hover {
          background: linear-gradient(135deg, #053e3e 0%, #064f4f 50%, #086161 100%) !important;
          color: white !important;
        }
        
        /* Кнопка отправки в неактивном состоянии */
        [data-n8n-chat] .n8n-chat-send-button:disabled,
        [data-n8n-chat] button[type="submit"]:disabled,
        [data-n8n-chat] .n8n-chat-footer button:disabled {
          background: #666666 !important;
          color: #999999 !important;
          opacity: 0.5 !important;
          cursor: not-allowed !important;
        }
        
        /* Сообщения пользователя - цвет логотипа */
        [data-n8n-chat] .n8n-chat-message--user,
        [data-n8n-chat] .user-message {
          background: linear-gradient(135deg, #064f4f 0%, #086161 50%, #0a7474 100%) !important;
          color: white !important;
        }
        
        /* Сообщения бота - светлый фон */
        [data-n8n-chat] .n8n-chat-message--bot,
        [data-n8n-chat] .bot-message {
          background: hsl(${backgroundColor}) !important;
          color: hsl(${foregroundColor}) !important;
          border: 1px solid hsl(${foregroundColor} / 0.1) !important;
        }
      `;
      
      // Добавляем отладку
      console.log('🎨 Применяем стили для N8N чата, цвета:', {
        primary: primaryColor,
        background: backgroundColor,
        foreground: foregroundColor
      });
      
      // Добавляем стили в head
      const styleSheet = document.createElement('style');
      styleSheet.textContent = chatStyles;
      document.head.appendChild(styleSheet);
      
      // Дополнительные агрессивные стили через setTimeout для применения после загрузки чата
      setTimeout(() => {
        const additionalStyles = `
          /* АГРЕССИВНЫЕ стили для исправления всего */
          * [data-n8n-chat] *,
          .n8n-chat *,
          #n8n-chat * {
            color: inherit !important;
          }
          
          /* Пользовательские сообщения - цвет логотипа */
          * [data-n8n-chat] .message--user *,
          * [data-n8n-chat] [class*="user"] *,
          .n8n-chat .message--user *,
          .n8n-chat [class*="user"] * {
            color: white !important;
            background: linear-gradient(135deg, #064f4f 0%, #086161 50%, #0a7474 100%) !important;
          }
          
          /* Кнопка отправки - цвет логотипа */
          * [data-n8n-chat] button,
          * [data-n8n-chat] [type="submit"],
          .n8n-chat button,
          .n8n-chat [type="submit"] {
            background: linear-gradient(135deg, #064f4f 0%, #086161 50%, #0a7474 100%) !important;
            color: white !important;
            border: none !important;
            opacity: 1 !important;
          }
          
          /* Поле ввода - контрастное */
          * [data-n8n-chat] input,
          * [data-n8n-chat] textarea,
          .n8n-chat input,
          .n8n-chat textarea {
            color: hsl(${foregroundColor}) !important;
            background: hsl(${backgroundColor}) !important;
            border: 1px solid hsl(${primaryColor}) !important;
          }
        `;
        
        const aggressiveStyleSheet = document.createElement('style');
        aggressiveStyleSheet.textContent = additionalStyles;
        document.head.appendChild(aggressiveStyleSheet);
        
        console.log('🔥 Применены агрессивные стили для N8N');
        
        // РАДИКАЛЬНЫЙ подход для кнопки отправки
        const forceButtonBehavior = () => {
          // Добавляем глобальные стили для всех состояний кнопки
          const buttonStyles = `
            /* КНОПКА ОТПРАВКИ - ПРИНУДИТЕЛЬНЫЕ СТИЛИ */
            .n8n-chat button[aria-label*="Send"],
            .n8n-chat button[type="submit"],
            .n8n-chat .send-button,
            [data-n8n-chat] button[aria-label*="Send"],
            [data-n8n-chat] button[type="submit"],
            [data-n8n-chat] .send-button,
            .n8n-chat-send-button {
              background: linear-gradient(135deg, #064f4f 0%, #086161 50%, #0a7474 100%) !important;
              color: white !important;
              border: none !important;
              opacity: 1 !important;
              transition: all 0.2s ease !important;
            }
            
            /* Неактивная кнопка - когда поле пустое */
            .n8n-chat button[disabled],
            .n8n-chat button:disabled,
            [data-n8n-chat] button[disabled],
            [data-n8n-chat] button:disabled,
            .n8n-chat-send-button:disabled {
              background: #333333 !important;
              color: #666666 !important;
              opacity: 0.3 !important;
              cursor: not-allowed !important;
            }
            
            /* ШРИФТ ЗАГОЛОВКА - ПРИНУДИТЕЛЬНО */
            .n8n-chat h1,
            .n8n-chat .title,
            .n8n-chat-title,
            [data-n8n-chat] h1,
            [data-n8n-chat] .title,
            [data-n8n-chat] .n8n-chat-title {
              font-family: 'Russo One', sans-serif !important;
              font-weight: 400 !important;
              letter-spacing: 0.5px !important;
            }
          `;
          
          const forceStyleSheet = document.createElement('style');
          forceStyleSheet.id = 'n8n-force-styles';
          forceStyleSheet.textContent = buttonStyles;
          document.head.appendChild(forceStyleSheet);
          
          // Проверяем и обновляем кнопки каждые 200мс
          const buttonChecker = setInterval(() => {
            const chatInputs = document.querySelectorAll('.n8n-chat input, .n8n-chat textarea, [data-n8n-chat] input, [data-n8n-chat] textarea');
            const chatButtons = document.querySelectorAll('.n8n-chat button, [data-n8n-chat] button');
            
            chatInputs.forEach((input) => {
              const inputEl = input as HTMLInputElement | HTMLTextAreaElement;
              
              chatButtons.forEach((button) => {
                const buttonEl = button as HTMLButtonElement;
                const hasText = inputEl.value && inputEl.value.trim().length > 0;
                
                if (hasText) {
                  buttonEl.disabled = false;
                  buttonEl.style.background = 'linear-gradient(135deg, #064f4f 0%, #086161 50%, #0a7474 100%)';
                  buttonEl.style.color = 'white';
                  buttonEl.style.opacity = '1';
                } else {
                  buttonEl.disabled = true;
                  buttonEl.style.background = '#333333';
                  buttonEl.style.color = '#666666';
                  buttonEl.style.opacity = '0.3';
                }
              });
            });
          }, 200);
          
          console.log('🚀 РАДИКАЛЬНЫЙ контроль кнопки активирован');
          
          // Останавливаем через 30 секунд
          setTimeout(() => clearInterval(buttonChecker), 30000);
        };
        
        forceButtonBehavior();
      }, 1000);

      // Получаем webhook URL из конфигурации или используем fallback
      const webhookUrl = config.VITE_N8N_WEBHOOK_URL || 'https://n8n.okta-solutions.com/webhook/a3c4f513-089f-4e78-95ef-1df42233522c/chat';
      
      // Создаем чат с кастомными настройками
      const chatInstance = createChat({
        webhookUrl,
        target: mountRef.current,
        mode: 'window',
        loadPreviousSession: false,
        showWelcomeScreen: true,
        initialMessages: [
          'Привет! 👋 Меня зовут Степан, я ваш AI-помощник.',
          'Готов помочь с любыми вопросами и задачами!'
        ],
        i18n: {
          en: {
            title: 'Степан',
            subtitle: 'Умный помощник для решения ваших задач',
            footer: '',
            getStarted: '✨ Начать диалог',
            inputPlaceholder: '💬 Задайте ваш вопрос...',
            closeButtonTooltip: 'Закрыть чат'
          }
        }
      });
      
      // Добавляем обработчик закрытия через DOM события
      if (onClose) {
        setTimeout(() => {
          const closeButton = document.querySelector('[data-n8n-chat] [aria-label*="close"], [data-n8n-chat] [aria-label*="Close"]');
          if (closeButton) {
            closeButton.addEventListener('click', onClose);
          }
        }, 1000);
      }
      
      chatInstanceRef.current = chatInstance;
      
    } catch (error) {
      console.error('Ошибка инициализации N8N чата:', error);
    }
  };

  useEffect(() => {
    initializeChat();

    return () => {
      if (chatInstanceRef.current) {
        try {
          chatInstanceRef.current = null;
        } catch (e) {
          console.warn('Не удалось очистить чат:', e);
        }
      }
    };
  }, [theme]);

  return <div ref={mountRef} />;
};

export default N8nChat;
